import {
  allocateAir,
  allocateAirMultiCity,
  finalizeAirShopping,
  finalizeAirShoppingMultiCity,
  makeAirPayment,
  makeAirPrebooking,
  makeAirPrebookingMultiCity,
  searchAir,
  updateAirPassengers,
} from '../../api/endpoints/air';
import type {
  AirBooking,
  AirSearchRequest,
  AirSearchResponse,
  AllocateItem,
  AllocateRequest,
  OriginDestinationOption,
  PaymentForm,
  PaymentResponse,
  PaymentType,
  SelectableFare,
} from '../../api/types/air';
import type { ShoppingCart } from '../../api/types/shoppingCart';
import type { Contact, Passenger } from '../../api/types/common';
import type { CurrentAccount } from '../../api/types/reports';
import { submitBilling } from '../../api/endpoints/billing';
import type { BillingInformation } from '../../api/types/billing';
import { extractContinueUrl, redirectToBankIfNeeded } from '../../components/payment/paymentRedirect';
import { cartIsPaid, type PersistedBookingFlowConfig } from '../../booking-flow/resume';
import { airPersistConfig, computeSearchDeadline } from './resume';
import { isMultiCityRequest } from './multiCity';
import { getShoppingCart } from '../../api/endpoints/shoppingCart';
import {
  createInsuranceOffer,
  createInsurancePolicy,
} from '../../api/endpoints/insurance';
import { ApiError } from '../../api/types/common';
import { buildInsuranceRequestsFromCart, readCartInsurance } from './insurance';

/**
 * Air booking flow state — migration guide Bölüm 6.1: Search → Allocate →
 * UpdatePassengers (+ MakePrebooking) → Koltuk Seçimi → MakePayment (+ FinalizeShopping)
 * → Document. Each step's response feeds ShoppingCartId/ShoppingCart into the next step's
 * request. Legacy `FlightController` gibi `runPayment`, başarılı (3DS olmayan) ödemeden
 * hemen sonra `Air/FinalizeShopping(MultiCity)`'yi aynı adımda zincirler; ayrı `finalize`
 * adımı yalnız o finalize başarısız kalırsa ya da ödenmiş-ama-biletlenmemiş bir sepetle
 * geri dönülürse elle tetiklenen bir yeniden-deneme noktasıdır.
 *
 * SCOPE: this config covers one-way, (domestic/international) round-trip AND multi-city
 * (çok noktalı). Multi-city reuses the SAME step array — `runSearch` sends the whole
 * multi-segment request to a single `Air/Search` (NOT `Air/SearchMulti` — see
 * api/types/air.ts header); `runAllocate` bir `Air/AllocateMultiCity`, prebooking ve
 * finalize ise sepet şekli / akış bağlamına göre `Air/*MultiCity` uçlarını seçer
 * (`shouldUseMultiCityEndpoint`) — tıpkı iç hat gidiş-dönüş yolunun
 * `state.selectedReturnOption`'a göre dallanması gibi. The
 * multi-city allocate/prebook/finalize REQUEST shapes are confirmed against live swagger
 * (2026-08-27: `Air/AllocateMultiCity` takes `AllocateRequest` with `allocateItems[]` +
 * an `isMultiCity` flag; per-leg results come back under
 * `AirSearchResponse.multiOriginDestinationOptions[legIndex]`, verified against a real
 * `Air/Search` MULTI response).
 *
 * SA-2 additions — branded-fare Upsell (`Air/Upsell`), one-way Combinable
 * (`Air/Search/Combinable`) and THY-NDC seat selection (`GetAdditionalServiceOffer` →
 * `Select/RemoveAdditionalService`) are now wired:
 *   - Upsell: `AirAllocateStep` (AirFlowPage) shows `AirUpsellPanel` after a single-list
 *     pick; choosing an alternative fare just swaps `selectedOption`/`selectedFare` before
 *     `runAllocate` (the upsell option's fresh `id` is a valid `AllocateItem.productId`).
 *   - Combinable: `AirResultsList`'s split (domestic round-trip) view offers a
 *     "Birleştirilebilir" return tab fed by `searchAirCombinable`; selection still flows
 *     through `onSelectRoundTrip` → two `AllocateItem`s in `runAllocate`.
 *   - Seat selection: `AirSeatSelectionStep` is the `seatSelection` step (THY-NDC only;
 *     disabled + notice otherwise). The general ancillary/baggage catalog
 *     (`Air/GetAdditionalServiceCatalog`, `AirBaggagePanel`) was removed — seat selection is
 *     the only ancillary flow kept.
 * These paths' REQUEST/RESPONSE shapes are written to the documented contract in
 * `Docs/api/02-air.md` and are LIVE-UNVERIFIED — this backend/test env has no flight
 * inventory (fresh `Air/Search` returns "no flights found"), so none of Upsell /
 * Combinable could be exercised end-to-end.
 *
 * Still out of scope: alternative flights, staged/group booking. Staged booking reuses
 * the existing `paymentResume` mechanism — stop after prebooking, resume payment from the
 * reservation list.
 */
export interface AirFlowState {
  searchRequest?: AirSearchRequest;
  searchResult?: AirSearchResponse;
  /** The itinerary the user picked from the results list, forwarded into Allocate.
   *  For a domestic round-trip (see AirResultsList's direction split), this is the
   *  GIDİŞ (outbound) selection; `selectedReturnOption` carries the DÖNÜŞ one. */
  selectedOption?: OriginDestinationOption;
  /** The specific priced fare (within selectedOption) the user picked — its
   *  `fareCode` is what Allocate actually needs, see api/types/air.ts header. */
  selectedFare?: SelectableFare;
  /** Domestic round-trip only — the independently-selected DÖNÜŞ (return) itinerary/
   *  fare (`FlightSegment.directionInd === 1`). Absent for one-way and international
   *  round-trip searches, which present a single combined itinerary per option. */
  selectedReturnOption?: OriginDestinationOption;
  selectedReturnFare?: SelectableFare;
  /** Çok noktalı (multi-city) arama — her bacak için bağımsız seçilen itinerary + ücret,
   *  index'i `searchRequest.airSearchSegments` sırasıyla aynı. Doluysa `runAllocate` ve
   *  prebooking/finalize adımları `Air/*MultiCity` uçlarını kullanır. */
  multiCitySelections?: { option: OriginDestinationOption; fare: SelectableFare }[];
  shoppingCart?: ShoppingCart | null;
  passengers?: Passenger[];
  contact?: Contact;
  /** Cari hesap (Report/FindCurrentAccount) — seçilirse, kurumsal cari ödeme için
   *  ileride payment adımına taşınacak; şu an sadece flow state'inde tutuluyor. */
  selectedCurrentAccount?: CurrentAccount;
  /** Yolcu bilgisi formunda derlenir, UpdatePassengers başarılı olduktan HEMEN SONRA
   *  Billing/Submit'e gönderilir (legacy `CreateBillingInformationRequest`'in
   *  `UpdatePassengersResponse` alması ile aynı sıra). */
  billingInformation?: BillingInformation;
  paymentForm?: PaymentForm;
  /** 'CurrentPayment' seçiliyse `paymentForm` gerekmiyor — cari hesap zaten
   *  Billing/Submit ile sepete bağlanmış oluyor (bkz. `runUpdatePassengers`). */
  paymentType?: PaymentType;
  paymentResult?: PaymentResponse;
  /** Aramadan sonra ödeme için son an — mutlak epoch (ms). `runSearch` doldurur,
   *  `AirFlowInner`'daki geri sayım bunu okur; taslakla birlikte saklanır. */
  searchDeadline?: number;
  /** Ödeme adımındaki "Sigorta İstiyorum" seçimi (`AirInsurancePanel`). `true` ise
   *  `runPayment` uçuş biletlendikten sonra `Insurance/CreateOffer` → 2. `MakePayment`
   *  (sigorta `TotalFare`) → `Insurance/CreatePolicy` zincirini best-effort çalıştırır
   *  (legacy `FlightController.MakeReservedPayment`). Taslakla saklanır. */
  wantsInsurance?: boolean;
  /** Sigorta zinciri başarısız olduysa kullanıcıya gösterilecek uyarı — uçuş biletlenmiş
   *  kalır, yalnız poliçe oluşmaz. Transient (persist edilmez). */
  insuranceError?: string;
  /** Kurumsal bütçe/politika dışı bir uçuş için yolcu adımı "Onaya Gönder" ile
   *  tamamlandı (legacy `FlightController.SendForApproval`: UpdatePassengers → Billing/Submit
   *  → MakePrebooking, sonra yöneticiye bildirim). `AirFlowInner` bunu görünce "onaya
   *  gönderildi" bilgisi verip rezervasyon listesine döner. Transient. */
  sentForApproval?: boolean;
}

function runSearch(state: AirFlowState): Promise<Partial<AirFlowState>> {
  if (!state.searchRequest) return Promise.reject(new Error('Arama kriterleri eksik.'));
  // Tek yön / gidiş-dönüş / çok noktalı — hepsi tek `Air/Search`. Çok noktalı istek
  // `flightType: 'MULTI'` + çok-segmentli `airSearchSegments` taşır; sonuçlar
  // `res.multiOriginDestinationOptions` altında bacak bazında gruplu döner.
  return searchAir(state.searchRequest).then((res) => ({
    searchResult: res,
    // sabit 15 dk yerine sağlayıcının en erken `expireAt`'ine göre (bkz. computeSearchDeadline)
    searchDeadline: computeSearchDeadline(res),
    // yeni arama önceki bacak seçimlerini geçersiz kılar
    multiCitySelections: undefined,
  }));
}

/** Çok noktalı: her bacağın seçili itinerary'si + ücreti bir `AllocateItem`'a dönüşür,
 *  hepsi tek çağrıda `Air/AllocateMultiCity`'ye (`isMultiCity: true`) gönderilir — backend
 *  tek bir ShoppingCart/AirBooking(lar) döndürür. */
function runAllocateMultiCity(
  selections: NonNullable<AirFlowState['multiCitySelections']>
): Promise<Partial<AirFlowState>> {
  const allocateItems: AllocateItem[] = [];
  for (const [i, sel] of selections.entries()) {
    const productId = sel.option.id ?? sel.option.flightSegments?.[0]?.productId;
    if (!productId) {
      return Promise.reject(new Error(`${i + 1}. uçuşun ürün kimliği bulunamadı.`));
    }
    if (!sel.fare) {
      return Promise.reject(new Error(`${i + 1}. uçuş için ücret seçeneği seçilmedi.`));
    }
    // `fareCode` yoksa (yalnız `PricingInfo`'lu GDS itinerary'si) `productId` tek başına
    // yeterli — bkz. api/types/air.ts `AllocateItem`.
    allocateItems.push(sel.fare.fareCode ? { productId, fareCode: sel.fare.fareCode } : { productId });
  }
  const body: AllocateRequest = { allocateItems, createShoppingCart: true, isMultiCity: true };
  return allocateAirMultiCity(body).then((res) => ({ shoppingCart: res.shoppingCart }));
}

function runAllocate(state: AirFlowState): Promise<Partial<AirFlowState>> {
  if (state.multiCitySelections?.length) {
    return runAllocateMultiCity(state.multiCitySelections);
  }
  if (!state.selectedOption) return Promise.reject(new Error('Bir uçuş seçilmedi.'));
  if (!state.selectedFare) return Promise.reject(new Error('Bir ücret seçeneği seçilmedi.'));
  // Confirmed via live curl: one AllocateItem keyed by the option's productId (equal
  // to its first flight segment's productId) + the chosen fare's fareCode is enough —
  // the backend expands it into all of that itinerary's connected segments server-side.
  // `selectedOption` may be an Upsell result (`Air/Upsell`) rather than a raw search hit —
  // same shape, its fresh `id` is cached server-side and is a valid `productId` here.
  const productId = state.selectedOption.id ?? state.selectedOption.flightSegments?.[0]?.productId;
  if (!productId) return Promise.reject(new Error('Seçilen uçuşun ürün kimliği bulunamadı.'));
  // `fareCode` yoksa (yalnız `PricingInfo`'lu GDS itinerary'si) `productId` tek başına
  // yeterli — bkz. api/types/air.ts `AllocateItem`.
  const allocateItems: AllocateItem[] = [
    state.selectedFare.fareCode ? { productId, fareCode: state.selectedFare.fareCode } : { productId },
  ];

  // Domestic round-trip (AirResultsList's direction-split view): gidiş + dönüş are
  // independently selected one-way itineraries, sent as two AllocateItems — the
  // backend combines them into one ShoppingCart/AirBooking.
  if (state.selectedReturnOption) {
    if (!state.selectedReturnFare) {
      return Promise.reject(new Error('Bir dönüş ücret seçeneği seçilmedi.'));
    }
    const returnProductId =
      state.selectedReturnOption.id ?? state.selectedReturnOption.flightSegments?.[0]?.productId;
    if (!returnProductId) return Promise.reject(new Error('Seçilen dönüş uçuşunun ürün kimliği bulunamadı.'));
    allocateItems.push(
      state.selectedReturnFare.fareCode
        ? { productId: returnProductId, fareCode: state.selectedReturnFare.fareCode }
        : { productId: returnProductId }
    );
  }

  const body: AllocateRequest = { allocateItems, createShoppingCart: true };
  return allocateAir(body).then((res) => ({ shoppingCart: res.shoppingCart }));
}

/**
 * Allocate pre-creates one passenger "slot" per pax (per `AirBooking.
 * passengerBreakdowns[].paxReference`), each carrying a server-generated
 * `paxReferanceId`. UpdatePassengers needs each outgoing `Passenger` stamped with the
 * id of the slot it fills — matched here by `paxType`/`paxCode` in allocation order
 * (e.g. 2x ADT breakdowns correlate to the 2 ADT rows in `state.passengers`, in the
 * same order the passenger form built them in). See api/types/air.ts's
 * UpdatePassengersRequest header for why this was previously missing.
 */
function withPaxReferenceIds(passengers: Passenger[], shoppingCart: AirFlowState['shoppingCart']): Passenger[] {
  const idQueueByCode = new Map<string, string[]>();
  const airBookings = (shoppingCart?.airBookings ?? []) as AirBooking[];
  for (const booking of airBookings) {
    for (const breakdown of booking.passengerBreakdowns ?? []) {
      const code = breakdown.paxReference?.paxCode;
      const id = breakdown.paxReference?.paxReferanceId;
      if (!code || !id) continue;
      const queue = idQueueByCode.get(code) ?? [];
      if (!queue.includes(id)) queue.push(id);
      idQueueByCode.set(code, queue);
    }
  }

  const cursorByCode = new Map<string, number>();
  return passengers.map((passenger) => {
    const code = passenger.paxType ?? 'ADT';
    const queue = idQueueByCode.get(code);
    if (!queue?.length) return passenger;
    const cursor = cursorByCode.get(code) ?? 0;
    cursorByCode.set(code, cursor + 1);
    const paxReferanceId = queue[cursor];
    return paxReferanceId ? { ...passenger, paxReferanceId } : passenger;
  });
}

/** Akışın çok noktalı olup olmadığı — `runAllocate` sonrası state'te `multiCitySelections`
 *  dolu kalır, prebooking/finalize adımları buna göre `Air/*MultiCity` uçlarını seçer. */
function isMultiCityState(state: AirFlowState): boolean {
  return !!state.multiCitySelections?.length || isMultiCityRequest(state.searchRequest);
}

/** `Model.AirBookings[0].FlightSegments[0].IsReservable` — legacy'de yalnız yolcu formu
 *  submit butonunun metnini ("Ön Rezervasyon" / "Ödemeye Devam Et") değiştiren UI ipucu
 *  (`_Note.cshtml`; `Allocated.cshtml`'deki buton bloğu yorum satırı). Prebooking'in
 *  çalışıp çalışmayacağını GATE'LEMEZ — bkz. `runUpdatePassengers`. */
export function isFirstBookingReservable(shoppingCart: AirFlowState['shoppingCart']): boolean {
  const airBookings = (shoppingCart?.airBookings ?? []) as AirBooking[];
  return airBookings[0]?.flightSegments?.[0]?.isReservable ?? false;
}

/**
 * `Air/*MultiCity` (prebooking + finalize) vs düz uç seçimi — legacy
 * `FlightController.UpdatePassenger` / `.MakePayment`'teki koşulun birebir karşılığı:
 * `AirBookings.Count > 1 && AirBookings.All(b => b.FlightSegments.All(s => s.DirectionInd == 0))`
 * (çok noktalı = bacak başına bir AirBooking, hepsi gidiş yönü; gidiş-dönüş dönüş
 * bacağını `DirectionInd == 1` ile ayırır, uluslararası gidiş-dönüş tek birleşik booking
 * verir → Count 1). Sepet şekli belirsiz/tek booking ise akış bağlamına düşülür
 * (`multiCitySelections` / `flightType: MULTI`) — URL ile geri dönülmüş (flow state'siz)
 * bir sepette de doğru uç seçilsin diye.
 */
function shouldUseMultiCityEndpoint(cart: AirFlowState['shoppingCart'], state: AirFlowState): boolean {
  const airBookings = (cart?.airBookings ?? []) as AirBooking[];
  if (airBookings.length > 1) {
    const allOutbound = airBookings.every(
      (b) => (b.flightSegments ?? []).every((s) => (s.directionInd ?? 0) === 0)
    );
    if (allOutbound) return true;
  }
  return isMultiCityState(state);
}

/**
 * Folds the old standalone "prebooking" step into the passengers step: after saving
 * passengers and submitting billing, ALWAYS chain a MakePrebooking call before advancing
 * (matches legacy `FlightController.UpdatePassenger` / `MakeReservation`, which prebook
 * unconditionally — the `IsReservable` flag is UI-only, see `isFirstBookingReservable`).
 * Multi-city carts go to `Air/MakePrebookingMultiCity` (see `shouldUseMultiCityEndpoint`).
 */
function runUpdatePassengers(state: AirFlowState): Promise<Partial<AirFlowState>> {
  if (!state.shoppingCart?.id) return Promise.reject(new Error('Sepet bulunamadı.'));
  if (!state.passengers?.length) return Promise.reject(new Error('Yolcu bilgisi eksik.'));
  if (!state.billingInformation) return Promise.reject(new Error('Fatura bilgisi eksik.'));
  const billingInformation = state.billingInformation;
  return updateAirPassengers({
    shoppingCartId: state.shoppingCart.id,
    passengers: withPaxReferenceIds(state.passengers, state.shoppingCart),
    contact: state.contact,
  })
    .then((res) => {
      // Legacy `CreateBillingInformationRequest` builds the billing request FROM the
      // UpdatePassengers response's ShoppingCart — Billing/Submit runs right after,
      // not before.
      const shoppingCart = res.shoppingCart ?? state.shoppingCart;
      if (!shoppingCart?.id) return Promise.resolve(shoppingCart);
      return submitBilling({ shoppingCartId: shoppingCart.id, billingInformation }).then(
        (billingRes) => billingRes.shoppingCart ?? shoppingCart
      );
    })
    .then((shoppingCart) => {
      if (!shoppingCart?.id) return { shoppingCart };
      const prebook = shouldUseMultiCityEndpoint(shoppingCart, state)
        ? makeAirPrebookingMultiCity
        : makeAirPrebooking;
      return prebook({ shoppingCartId: shoppingCart.id }).then((preRes) => {
        // `HasError: false` + `ShoppingCart: null` de dönebiliyor — sessizce eski sepetle
        // devam etmek yerine (üstünde ön rezervasyon yokken ödemeye geçmiş oluruz) hata
        // fırlatıp adımı bloke ediyoruz.
        if (!preRes.shoppingCart) throw new Error('Ön rezervasyon oluşturulamadı — sepet boş döndü.');
        return { shoppingCart: preRes.shoppingCart };
      });
    });
}

/** `Air/FinalizeShopping` (`MakeBooking`) / `Air/FinalizeShoppingMultiCity`
 *  (`MakeBookingMultiCity`) — biletleme. `runPayment` başarılı (3DS olmayan) ödemeden
 *  hemen sonra bunu zaten çağırır (legacy `FlightController.MakePayment` ile aynı); bu
 *  ayrı adım yalnız ödeme sonrası finalize başarısız kalırsa veya rezervasyon
 *  listesinden ödenmiş-ama-biletlenmemiş bir sepetle geri dönülürse elle tetiklenir. */
function runFinalize(state: AirFlowState): Promise<Partial<AirFlowState>> {
  if (!state.shoppingCart?.id) return Promise.reject(new Error('Sepet bulunamadı.'));
  const finalize = shouldUseMultiCityEndpoint(state.shoppingCart, state)
    ? finalizeAirShoppingMultiCity
    : finalizeAirShopping;
  return finalize({
    shoppingCartId: state.shoppingCart.id,
    linkUrl: window.location.origin,
    sendInformationEmails: true,
  }).then((res) => ({ shoppingCart: res.shoppingCart ?? state.shoppingCart }));
}

/**
 * Uçuş biletlendikten sonra sigorta zinciri (legacy `FlightController.MakeReservedPayment`
 * 3601-3690): `Insurance/CreateOffer` (sepete `shoppingCartId` ile bağlı) → sepeti tazele →
 * 2. `MakePayment` (`Amount == TravelInsurances[0].TotalFare`, aynı kart) → `Insurance/
 * CreatePolicy`. Best-effort: her hata `insuranceError`'a düşer, uçuş biletlenmiş kalır.
 */
async function runInsuranceChain(
  state: AirFlowState,
  paidCart: ShoppingCart,
  paymentType: PaymentType
): Promise<void> {
  const reqs = buildInsuranceRequestsFromCart(paidCart);
  if (!reqs || !paidCart.id) throw new Error('Sigorta için yolcu/uçuş bilgisi eksik.');

  const offer = await createInsuranceOffer({ ...reqs.createOffer, shoppingCartId: paidCart.id });
  const teklifId = offer.data?.teklifBilgileri?.teklifId;
  if (!teklifId) {
    throw new Error(offer.data?.errorMessage || 'Sigorta teklifi oluşturulamadı.');
  }

  const fresh = (await getShoppingCart(paidCart.id)).shoppingCart ?? paidCart;
  const insTotal = readCartInsurance(fresh).totalFare ?? offer.data?.teklifBilgileri?.fiyatFloat;
  if (insTotal == null) throw new Error('Sigorta tutarı belirlenemedi.');

  // `Amount == insurance TotalFare` → PaymentWorker sigorta branch'i (PaymentWorker.cs:471).
  await makeAirPayment({
    shoppingCartId: paidCart.id,
    amount: insTotal,
    paymentType,
    paymentForm: paymentType === 'CurrentPayment' ? undefined : state.paymentForm,
  });

  await createInsurancePolicy({ teklifId: String(teklifId), shoppingCart: fresh });
}

function runPayment(state: AirFlowState): Promise<Partial<AirFlowState>> {
  if (!state.shoppingCart?.id) return Promise.reject(new Error('Sepet bulunamadı.'));
  // Yenilenmiş/geri dönülmüş bir akışta ödenmiş sepete ikinci kez ödeme denenmesin.
  if (cartIsPaid(state.shoppingCart)) {
    return Promise.reject(new Error('Bu rezervasyon zaten ödenmiş.'));
  }
  const paymentType = state.paymentType ?? 'CreditCardPayment';
  if (paymentType !== 'CurrentPayment' && !state.paymentForm) {
    return Promise.reject(new Error('Ödeme bilgisi eksik.'));
  }
  return makeAirPayment({
    shoppingCartId: state.shoppingCart.id,
    amount: state.shoppingCart.remainingTotal ?? 0,
    paymentType,
    // Cari (CurrentPayment): kart bilgisi yok — hesap zaten Billing/Submit'te sepete
    // bağlandı, sadece paymentType ile hangi yöntemin seçildiği bildiriliyor.
    paymentForm: paymentType === 'CurrentPayment' ? undefined : state.paymentForm,
  }).then((res) => {
    // 3D Secure: MakePayment döndü ama sepet kesinleşmedi — banka onay URL'i geldi.
    // Tarayıcı bankaya yönlendiriliyor; sayfa boşalacağı için asla çözülmeyen bir
    // promise dönüyoruz ki flow "running" kalsın ve adım ilerlemesin. Sigorta istendiyse
    // flow state bankada kaybolacağı için niyeti sessionStorage'a bırak — dönüş sayfası
    // (`PaymentReturnPage` → `AirInsuranceReturnPanel`) oradan devam eder. PII yok; yolcu/
    // tarih bilgisi ödenmiş sepetten yeniden kurulur.
    if (extractContinueUrl(res) && state.wantsInsurance && state.shoppingCart?.id) {
      try {
        sessionStorage.setItem(`air:pendingInsurance:${state.shoppingCart.id}`, '1');
      } catch {
        /* özel sekme / storage kapalı — sigorta dönüş sayfasında sunulamaz */
      }
    }
    if (redirectToBankIfNeeded(res)) return new Promise<Partial<AirFlowState>>(() => {});

    const paidCart = res.shoppingCart ?? state.shoppingCart;
    if (!paidCart?.id) return { paymentResult: res, shoppingCart: paidCart };

    // Legacy `FlightController.MakePayment`: başarılı (3DS olmayan) ödemeden HEMEN SONRA
    // aynı akışta `Air/FinalizeShopping` / `Air/FinalizeShoppingMultiCity` çağrılır (sepet
    // şekli belirler). Finalize hatası ödemeyi geri almaz — `paymentResult` yine state'e
    // yazılır (adım "error"a düşüp kaybolmasın); kullanıcı 'finalize' adımından tekrar dener.
    const finalize = shouldUseMultiCityEndpoint(paidCart, state)
      ? finalizeAirShoppingMultiCity
      : finalizeAirShopping;
    return finalize({
      shoppingCartId: paidCart.id,
      linkUrl: window.location.origin,
      sendInformationEmails: true,
    })
      .then(async (finRes) => {
        const finalizedCart = finRes.shoppingCart ?? paidCart;
        // Uçuş biletlendi. Sigorta istendiyse best-effort zincir — başarısızsa uçuş yine biletli.
        if (!state.wantsInsurance) return { paymentResult: res, shoppingCart: finalizedCart };
        try {
          await runInsuranceChain(state, finalizedCart, paymentType);
          return { paymentResult: res, shoppingCart: finalizedCart };
        } catch (err) {
          const msg =
            err instanceof ApiError || err instanceof Error ? err.message : 'Sigorta poliçesi oluşturulamadı.';
          return { paymentResult: res, shoppingCart: finalizedCart, insuranceError: msg };
        }
      })
      .catch(() => ({ paymentResult: res, shoppingCart: paidCart }));
  });
}

/** No API call of its own — `AirSeatSelectionStep` drives `Air/GetAdditionalServiceOffer`
 *  + `Air/SelectAdditionalService`/`Air/RemoveAdditionalService` directly (see that
 *  component; it patches the flow's `shoppingCart` itself via `patchState` as seats are
 *  selected/removed), this just advances the flow once the admin clicks "Devam Et". Only
 *  shows real seat-selection UI when the booking's provider is `'THYNDC'` (Turkish
 *  Airlines NDC) — every other provider sees an informational passthrough. Placed right
 *  before `payment`, per the actual requirement (seat selection must happen before
 *  payment). */
function runSeatSelection(): Promise<Partial<AirFlowState>> {
  return Promise.resolve({});
}

export const airFlowConfig: PersistedBookingFlowConfig<AirFlowState> = {
  id: 'air',
  initialState: {},
  persist: airPersistConfig,
  steps: [
    { id: 'search', label: 'Arama', run: runSearch },
    { id: 'allocate', label: 'Uçuş Seçimi', run: runAllocate },
    { id: 'passengers', label: 'Yolcu Bilgisi', run: runUpdatePassengers },
    { id: 'seatSelection', label: 'Koltuk Seçimi', run: runSeatSelection },
    { id: 'payment', label: 'Ödeme', run: runPayment },
    { id: 'finalize', label: 'Kesinleştirme', run: runFinalize },
  ],
};
